import api from '@/lib/api';
import type { SentinelTicketSection, SentinelTicketStatus, SentinelViewCounts } from '@/hooks/use-sentinel-routing';
import { API_ENDPOINTS } from '@/constants/endpoints';
import { withBuScope } from '@/lib/business-unit-scope';
import { SentinelWorkspaceAdapter } from '@/adapters/sentinelWorkspace.adapter';
import { DEFAULT_INACTIVITY_RELEASE_MINUTES } from '@/modules/sentinel-workspace/types';
import type {
  CustomerTicketPageDto,
  PendingActionsDto,
  ProviderAgentPageDto,
  RejectReason,
  SentinelBlueprintDto,
  SentinelBrandDto,
  SentinelBrandCapDto,
  SentinelUserBrandDto,
  SentinelSettingsDto,
  SentinelSettingsWireDto,
  SentinelOutboundAttachment,
  SentinelOutboundAttachmentWireDto,
  SentinelTicketListWireDto,
  SentinelWorkspaceDetailWireDto,
  SimilarTicketThreadDto,
  TicketTagsDto,
  FeedbackVerdict,
  GateCheckDto,
  SendGateConfigDto,
} from '@/modules/sentinel-workspace/types';
import type { TicketFieldValue } from '@/types';
import { storageService } from '@/services/storage.service';
import { BLUEPRINT_REFINEMENT_NOT_APPLIED, sentinelErrorMessage } from '@/modules/sentinel-workspace/error-copy';
import { sentinelAttachmentContentType } from '@/modules/sentinel-workspace/attachment-formats';

interface ApiEnvelope<T> {
  data: T;
}

export interface RejectInput {
  stage: 'blueprint' | 'response' | 'error';
  reason: RejectReason;
  otherText: string | null;
  providerAgentId: string | null;
  /** Written to the helpdesk ticket before it is handed back. */
  tags?: string[];
  note?: string | null;
}

/** What the helpdesk would not take with the handoff; the ticket was handed off regardless. */
export interface RejectResult {
  tagsNotAdded?: string[];
  noteNotAdded?: boolean;
}

function settingsFromWire(settings: SentinelSettingsWireDto | null): SentinelSettingsDto {
  return {
    // A missing row means the account never configured Sentinel; a stored row keeps its flag.
    dashboardEnabled: settings !== null && settings.enabled !== false,
    sendResponsesTo: settings?.mode === 'live' ? 'direct_to_customer' : 'client_review_dashboard',
    maxResponseRefines: settings?.max_response_retries ?? 10,
    blueprintEnabled: settings?.blueprint_enabled !== false,
    maxBlueprintRefines: settings?.max_blueprint_retries ?? 2,
    providerAssignees: settings?.provider_assignees ?? {},
    routingEnabled: settings?.routing_enabled === true,
    sendHoldsCapacity: settings?.send_holds_capacity === true,
    inactivityReleaseMinutes: settings?.inactivity_release_minutes ?? DEFAULT_INACTIVITY_RELEASE_MINUTES,
    defaultMaxActiveTickets: settings?.default_max_active_tickets ?? null,
    sendGate: { enabled: settings?.sendGate?.enabled === true },
  };
}

const actionHeaders = (idempotencyKey: string = crypto.randomUUID()) => ({
  headers: { 'Idempotency-Key': idempotencyKey },
});
const delay = (milliseconds: number) => new Promise((resolve) => setTimeout(resolve, milliseconds));

const BLUEPRINT_NOT_READY = 'Sentinel blueprint is not available yet';
const ACTIVE_ARTIFACT_STATUSES = new Set(['pending', 'response_generating', 'awaiting_hitl']);
const TERMINAL_ARTIFACT_STATUSES = new Set([
  'awaiting_hitl',
  'response_generated',
  'blueprint_generated',
  'awaiting_customer',
  'failed',
  'unassigned_by_agent',
  'closed_in_helpdesk',
]);

interface SentinelDispatchResult {
  status: 'completed' | 'dispatch_unknown' | 'gate_blocked';
  providerMessageId: string | null;
  errorCode?: string;
  /** With `gate_blocked`, when the Send itself ran the check. */
  check?: GateCheckDto;
}

export type SendGateErrorCode = 'GATE_BLOCKED' | 'GATE_STALE';

/** A Send either went out or was paused by the send gate; anything else throws. */
export type SendResponseOutcome =
  | { status: 'completed' }
  | { status: 'gate_blocked'; errorCode: SendGateErrorCode; check: GateCheckDto | null };

interface ArtifactWaitOptions {
  requestId?: string;
  timeoutMs?: number;
  onTick?: (detail: SentinelWorkspaceDetailWireDto) => void;
}

interface AttemptError {
  code: string;
  detail: string | null;
  eventId: number | null;
}

function record(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

function attemptError(detail: SentinelWorkspaceDetailWireDto, requestId: string): AttemptError | null {
  const attempt = detail.aiEventLogs.find(
    (event) => event.request_id === requestId || event.trace_id === requestId,
  );
  const extra = record(attempt?.extra_details);
  const code = extra?.['error_code'];
  if (typeof code !== 'string' || !code) return null;
  const message = extra?.['error_message'];
  return {
    code,
    detail: typeof message === 'string' && message ? message : null,
    eventId: attempt?.id ?? null,
  };
}

export class SentinelWorkspaceError extends Error {
  // Keep fields explicit for erasableSyntaxOnly.
  readonly code: string | null;
  readonly detail: string | null;

  constructor(message: string, code: string | null = null, detail: string | null = null) {
    super(message);
    this.name = 'SentinelWorkspaceError';
    this.code = code;
    this.detail = detail;
  }
}

/** Read the latest persisted blueprint. */
function blueprintFromDetail(detail: SentinelWorkspaceDetailWireDto): SentinelBlueprintDto {
  const event = [...detail.aiEventLogs]
    .filter((item) => item.event_kind === 'blueprint_revision')
    .sort((left, right) => (right.blueprint_revision_number ?? 0) - (left.blueprint_revision_number ?? 0))[0];
  const review = event?.extra_details?.['blueprintReview'] as Record<string, unknown> | undefined;
  const blueprints = review?.['blueprints'];
  const blueprint = Array.isArray(blueprints) ? (blueprints[0] as SentinelBlueprintDto['blueprint'] | undefined) : undefined;
  if (!event || !blueprint) throw new Error(BLUEPRINT_NOT_READY);
  return {
    blueprintId: event.trace_id,
    conversationId: detail.ticket.conversation_id,
    version: event.blueprint_revision_number ?? 1,
    blueprint,
  };
}

export const sentinelWorkspaceService = {
  // Scoped to the selected brand. This used to show the whole account and label each row
  // instead; the switcher now filters the queue, so an operator working one brand sees
  // that brand's tickets only. "All brands" sends no bu_id and still returns everything.
  //
  // With auto-routing on, `section` picks the view (mine, all, unassigned, automation) and
  // `ticketStatus` narrows it to open or closed tickets. Without routing the list is the
  // original unsectioned queue.
  async getTickets(
    limit: number,
    offset: number,
    scope: { bu_id?: string } = {},
    section?: SentinelTicketSection,
    search?: string,
    ticketStatus?: SentinelTicketStatus,
  ): Promise<SentinelTicketListWireDto> {
    const response = await api.get<ApiEnvelope<SentinelTicketListWireDto>>(API_ENDPOINTS.SENTINEL.TICKETS, {
      params: {
        limit,
        offset,
        ...scope,
        ...(section ? { section } : {}),
        ...(search ? { search } : {}),
        ...(ticketStatus ? { ticket_status: ticketStatus } : {}),
      },
    });
    return response.data.data;
  },

  /**
   * The details panel's data for any conversation: its Sentinel ticket's detail, or the same shape
   * built from the conversation when it never reached Sentinel (then `sentinelTicketId` is null).
   */
  async getConversationContext(
    conversationId: number,
  ): Promise<{ sentinelTicketId: number | null; automated?: boolean; detail: SentinelWorkspaceDetailWireDto }> {
    const response = await api.get<ApiEnvelope<{ sentinelTicketId: number | null; automated?: boolean; detail: SentinelWorkspaceDetailWireDto }>>(
      API_ENDPOINTS.SENTINEL.CONVERSATION_CONTEXT(conversationId),
    );
    return response.data.data;
  },

  async getConversation(ticketId: number, includeMessages = true): Promise<SentinelWorkspaceDetailWireDto> {
    const response = await api.get<ApiEnvelope<SentinelWorkspaceDetailWireDto>>(
      API_ENDPOINTS.SENTINEL.TICKET(ticketId),
      includeMessages ? undefined : { params: { includeMessages: false } },
    );
    return response.data.data;
  },

  async getAttachmentBlob(ticketId: number, messageId: number, attachmentIndex: number): Promise<Blob> {
    const response = await api.get<Blob>(API_ENDPOINTS.SENTINEL.ATTACHMENT(ticketId, messageId, attachmentIndex), {
      responseType: 'blob',
    });
    return response.data;
  },

  async uploadResponseAttachments(ticketId: number, files: File[]): Promise<SentinelOutboundAttachment[]> {
    return Promise.all(
      files.map(async (file) => {
        const contentType = sentinelAttachmentContentType(file);
        if (!contentType) throw new Error('Unsupported Sentinel attachment format');
        const response = await api.post<
          ApiEnvelope<{ uploadUrl: string; attachment: SentinelOutboundAttachmentWireDto }>
        >(API_ENDPOINTS.SENTINEL.ATTACHMENT_UPLOAD(ticketId), {
          fileName: file.name,
          contentType,
          fileSize: file.size,
        });
        const { uploadUrl, attachment } = response.data.data;
        const uploadFile = file.type ? file : new File([file], file.name, { type: contentType });
        await storageService.uploadFile(uploadUrl, uploadFile);
        return SentinelWorkspaceAdapter.outboundAttachmentFromApi(attachment);
      }),
    );
  },

  async acceptBlueprint(ticketId: number, aiEventLogId: number): Promise<{ requestId: string }> {
    const response = await api.post<ApiEnvelope<{ requestId: string }>>(API_ENDPOINTS.SENTINEL.ACCEPT_BLUEPRINT(ticketId), {
      aiEventLogId,
    });
    return response.data.data;
  },

  async createBlueprintFeedback(
    ticketId: number,
    input: { aiEventLogId: number; verdict: 'yes' | 'no'; rewrite?: string },
  ): Promise<unknown> {
    const response = await api.post<ApiEnvelope<unknown>>(API_ENDPOINTS.SENTINEL.BLUEPRINT_FEEDBACK(ticketId), {
      ...input,
      ...(input.verdict === 'no' ? { revisionTarget: 'blueprint' } : {}),
    });
    return response.data.data;
  },

  async getSettings(scope?: { bu_id?: string }): Promise<SentinelSettingsDto> {
    // What is IN FORCE for the brand: its own row, or the account's if it has none.
    const response = await api.get<ApiEnvelope<SentinelSettingsWireDto | null>>(
      withBuScope(API_ENDPOINTS.SENTINEL.SETTINGS, scope ?? {}),
    );
    return settingsFromWire(response.data.data);
  },

  /**
   * What is in force for ONE TICKET.
   *
   * The composer's retry caps have to come from the ticket, not the brand switcher. A
   * Sentinel's own queue spans every brand they cover, so an agent working as brand A can
   * have a brand B ticket open, and the backend enforces the caps of whichever brand the
   * CONVERSATION belongs to. The server resolves that from the conversation id, so the two
   * cannot drift.
   */
  async getSettingsForConversation(conversationId: number): Promise<SentinelSettingsDto> {
    const response = await api.get<ApiEnvelope<SentinelSettingsWireDto | null>>(
      `${API_ENDPOINTS.SENTINEL.SETTINGS}?conversation_id=${conversationId}`,
    );
    return settingsFromWire(response.data.data);
  },

  async updateSettings(settings: SentinelSettingsDto, scope?: { bu_id?: string }): Promise<SentinelSettingsDto> {
    // Disable is a flag, not a delete — the account's mode, caps and assignees survive it.
    //
    // Saving under a brand writes THAT brand's row, so a brand that was inheriting the
    // account's settings stops inheriting from here on. That is what the operator asked
    // for by saving while a brand is selected.
    const response = await api.put<ApiEnvelope<SentinelSettingsWireDto>>(
      withBuScope(API_ENDPOINTS.SENTINEL.SETTINGS, scope ?? {}),
      {
        mode: settings.sendResponsesTo === 'direct_to_customer' ? 'live' : 'review',
        providerAssignees: settings.providerAssignees,
        maxBlueprintRetries: settings.maxBlueprintRefines,
        maxResponseRetries: settings.maxResponseRefines,
        enabled: settings.dashboardEnabled,
        blueprintEnabled: settings.blueprintEnabled,
        sendHoldsCapacity: settings.sendHoldsCapacity,
        inactivityReleaseMinutes: settings.inactivityReleaseMinutes,
        defaultMaxActiveTickets: settings.defaultMaxActiveTickets,
      },
    );
    return settingsFromWire(response.data.data);
  },

  /** Administrators only. Every user's brands and cap in each, keyed by user id. */
  async getUserBrands(): Promise<Record<string, SentinelUserBrandDto[]>> {
    const response = await api.get<ApiEnvelope<Record<string, SentinelUserBrandDto[]>>>(
      API_ENDPOINTS.SENTINEL.USER_BRANDS,
    );
    return response.data.data;
  },

  /** Administrators only. Sets exactly which brands a Sentinel works, and their cap in each. */
  async saveUserBrands(userId: number, brands: SentinelUserBrandDto[]): Promise<SentinelUserBrandDto[]> {
    const response = await api.put<ApiEnvelope<SentinelUserBrandDto[]>>(
      API_ENDPOINTS.SENTINEL.USER_BRANDS_OF(userId),
      { brands },
    );
    return response.data.data;
  },

  /** Administrators only. Each active brand's cap for every Sentinel, as it applies. */
  async getBrandCaps(): Promise<SentinelBrandCapDto[]> {
    const response = await api.get<ApiEnvelope<SentinelBrandCapDto[]>>(API_ENDPOINTS.SENTINEL.BRAND_CAPS);
    return response.data.data;
  },

  /** Administrators only. Puts every Sentinel in the brand back on the brand's cap; returns how many changed. */
  async clearOwnCaps(scope: { bu_id: string }): Promise<number> {
    const response = await api.delete<ApiEnvelope<{ cleared: number }>>(
      withBuScope(API_ENDPOINTS.SENTINEL.BRAND_OWN_CAPS, scope),
    );
    return response.data.data.cleared;
  },

  /** Unassigned tickets per brand id: the signed-in Sentinel's brands, or every brand for an admin. */
  /** Open and closed tickets in each view of one brand, for the view switch and the status filter. */
  async getViewCounts(scope: { bu_id: string }): Promise<SentinelViewCounts> {
    const response = await api.get<ApiEnvelope<SentinelViewCounts>>(API_ENDPOINTS.SENTINEL.VIEW_COUNTS, { params: scope });
    return response.data.data;
  },

  async getUnassignedCounts(): Promise<Record<string, number>> {
    const response = await api.get<ApiEnvelope<Record<string, number>>>(API_ENDPOINTS.SENTINEL.UNASSIGNED_COUNTS);
    return response.data.data;
  },

  /**
   * Per brand: the selected brand's switch, or with no brand the account's, which brands
   * without settings of their own inherit.
   */
  async setRoutingEnabled(enabled: boolean, scope: { bu_id?: string } = {}): Promise<boolean> {
    const response = await api.put<ApiEnvelope<SentinelSettingsWireDto>>(
      withBuScope(API_ENDPOINTS.SENTINEL.ROUTING, scope),
      { enabled },
    );
    return response.data.data.routing_enabled === true;
  },

  /** Whether any brand in the account routes; the workspace shows its routed sections when one does. */
  async getRoutingStatus(): Promise<boolean> {
    const response = await api.get<ApiEnvelope<{ active: boolean }>>(API_ENDPOINTS.SENTINEL.ROUTING);
    return response.data.data.active === true;
  },

  /** Administrators only. User ids of the Sentinels online in the brand right now. */
  async getPresence(scope: { bu_id: string }): Promise<number[]> {
    const response = await api.get<ApiEnvelope<{ onlineUserIds: number[] }>>(
      withBuScope(API_ENDPOINTS.SENTINEL.PRESENCE, scope),
    );
    return response.data.data.onlineUserIds;
  },

  /** The signed-in Sentinel's own Online/Offline switch in the brand. One switch per brand. */
  async getAvailability(scope: { bu_id: string }): Promise<boolean> {
    const response = await api.get<ApiEnvelope<{ online: boolean }>>(
      withBuScope(API_ENDPOINTS.SENTINEL.AVAILABILITY, scope),
    );
    return response.data.data.online;
  },

  async setAvailability(scope: { bu_id: string }, online: boolean): Promise<boolean> {
    const response = await api.put<ApiEnvelope<{ online: boolean }>>(
      withBuScope(API_ENDPOINTS.SENTINEL.AVAILABILITY, scope),
      { online },
    );
    return response.data.data.online;
  },

  /** The brands the signed-in Sentinel covers, for the workspace brand switcher. */
  async getMyBrands(): Promise<SentinelBrandDto[]> {
    const response = await api.get<ApiEnvelope<SentinelBrandDto[]>>(API_ENDPOINTS.SENTINEL.MY_BRANDS);
    return response.data.data;
  },

  async getBlueprint(ticketId: number): Promise<SentinelBlueprintDto> {
    return blueprintFromDetail(await this.getConversation(ticketId, false));
  },

  async refineBlueprint(
    ticketId: number,
    feedback: string,
    _currentVersion: number,
    onStarted?: (requestId: string) => void,
  ): Promise<SentinelBlueprintDto> {
    const accepted = await api.post<ApiEnvelope<{ requestId: string }>>(
      API_ENDPOINTS.SENTINEL.BLUEPRINT_RETRY(ticketId),
      { prompt: feedback },
      actionHeaders(),
    );
    onStarted?.(accepted.data.data.requestId);
    return this.waitForBlueprintVersion(ticketId, _currentVersion);
  },

  async waitForBlueprintVersion(ticketId: number, priorVersion: number, timeoutMs = 90_000): Promise<SentinelBlueprintDto> {
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
      const detail = await this.getConversation(ticketId, false);
      // Stop early when the retry has failed.
      const failed =
        detail.ticket.status === 'failed' || detail.ticket.last_error_code === BLUEPRINT_REFINEMENT_NOT_APPLIED;
      if (failed) {
        throw new SentinelWorkspaceError(
          sentinelErrorMessage(detail.ticket.last_error_code, 'blueprint'),
          detail.ticket.last_error_code,
          detail.ticket.last_error_detail,
        );
      }
      try {
        const blueprint = blueprintFromDetail(detail);
        if (blueprint.version > priorVersion) return blueprint;
      } catch (error) {
        // Keep polling only while the blueprint is not persisted.
        if (!(error instanceof Error) || error.message !== BLUEPRINT_NOT_READY) throw error;
      }
      await delay(800);
    }
    throw new Error('Timed out waiting for the Sentinel blueprint');
  },

  async waitForArtifact(
    ticketId: number,
    priorEventId: number | null,
    options: ArtifactWaitOptions = {},
  ): Promise<SentinelWorkspaceDetailWireDto> {
    const { requestId, timeoutMs = 120_000, onTick } = options;
    const deadline = Date.now() + timeoutMs;
    let sawGeneration = false;
    while (Date.now() < deadline) {
      const detail = await this.getConversation(ticketId, false);
      onTick?.(detail);
      if (ACTIVE_ARTIFACT_STATUSES.has(detail.ticket.status)) {
        sawGeneration = true;
      }
      const changed = detail.ticket.current_ai_event_log_id !== priorEventId;
      const failed = requestId ? Boolean(attemptError(detail, requestId)) : false;
      const requestSettled = requestId
        ? failed ||
          (detail.ticket.active_generation_request_id === requestId &&
            TERMINAL_ARTIFACT_STATUSES.has(detail.ticket.status))
        : false;
      const settled = detail.ticket.status !== 'awaiting_hitl' || changed || requestSettled || failed;
      // The ticket can point at a new event before that event carries its payload.
      const artifactReady =
        TERMINAL_ARTIFACT_STATUSES.has(detail.ticket.status) &&
        settled &&
        (changed || sawGeneration || requestSettled) &&
        (failed ||
          detail.ticket.status === 'closed_in_helpdesk' ||
          SentinelWorkspaceAdapter.artifactFromApi(detail).kind !== 'working');
      if (artifactReady) {
        return detail;
      }
      await delay(800);
    }
    throw new Error('Timed out waiting for Sentinel');
  },

  async generateResponse(ticketId: number, onStarted?: (requestId: string) => void): Promise<SentinelWorkspaceDetailWireDto> {
    const detail = await this.getConversation(ticketId);
    const eventId = detail.ticket.current_ai_event_log_id;
    // Retry when there is no blueprint to accept.
    const currentEvent = detail.aiEventLogs.find((event) => event.id === eventId);
    const hasRevision = currentEvent?.event_kind === 'blueprint_revision';
    if (!hasRevision) {
      const retried = await api.post<ApiEnvelope<{ requestId: string }>>(
        API_ENDPOINTS.SENTINEL.BLUEPRINT_RETRY(ticketId),
        { prompt: 'Retry the blueprint generation for this ticket.' },
        actionHeaders(),
      );
      onStarted?.(retried.data.data.requestId);
      return this.waitForArtifact(ticketId, eventId, { requestId: retried.data.data.requestId });
    }
    if (!eventId) throw new Error('Current Sentinel blueprint is missing');
    const accepted = await api.post<ApiEnvelope<{ requestId: string }>>(API_ENDPOINTS.SENTINEL.ACCEPT_BLUEPRINT(ticketId), {
      aiEventLogId: eventId,
    });
    onStarted?.(accepted.data.data.requestId);
    return this.waitForArtifact(ticketId, eventId, { requestId: accepted.data.data.requestId });
  },

  async refineResponse(
    ticketId: number,
    input: { command?: string; prompt?: string },
    onStarted?: (requestId: string) => void,
    onProgress?: (detail: SentinelWorkspaceDetailWireDto) => void,
  ): Promise<SentinelWorkspaceDetailWireDto> {
    const before = await this.getConversation(ticketId);
    const priorEventId = before.ticket.current_ai_event_log_id;
    const priorErrorCode = before.ticket.last_error_code;
    const priorErrorDetail = before.ticket.last_error_detail;
    // Use a new key so retries run again.
    const accepted = await api.post<ApiEnvelope<{ requestId: string }>>(
      API_ENDPOINTS.SENTINEL.REFINE_RESPONSE(ticketId),
      { ...input, command: input.command ?? 'generate' },
      actionHeaders(),
    );
    onStarted?.(accepted.data.data.requestId);

    const detail = await this.waitForArtifact(ticketId, priorEventId, {
      requestId: accepted.data.data.requestId,
      onTick: onProgress,
    });
    const failedAttempt = attemptError(detail, accepted.data.data.requestId);
    if (failedAttempt) {
      throw new SentinelWorkspaceError(
        sentinelErrorMessage(detail.ticket.last_error_code ?? failedAttempt.code, 'response'),
        detail.ticket.last_error_code ?? failedAttempt.code,
        detail.ticket.last_error_detail ?? failedAttempt.detail,
      );
    }
    // Do not present the old draft as a new version.
    const hasNewError =
      detail.ticket.last_error_detail &&
      (detail.ticket.last_error_code !== priorErrorCode || detail.ticket.last_error_detail !== priorErrorDetail);
    if (detail.ticket.current_ai_event_log_id === priorEventId && hasNewError) {
      throw new SentinelWorkspaceError(
        sentinelErrorMessage(detail.ticket.last_error_code, 'response'),
        detail.ticket.last_error_code,
        detail.ticket.last_error_detail,
      );
    }
    return detail;
  },

  async executeAction(ticketId: number, approvalId: string, onStarted?: (requestId: string) => void): Promise<SentinelWorkspaceDetailWireDto> {
    return this.submitHitlDecision(ticketId, { approvalId, decision: 'approve' }, onStarted);
  },

  /** Turn the action down and tell Kim what to do instead; it answers with a reply to edit. */
  async rejectAction(
    ticketId: number,
    approvalId: string,
    notes: string,
    onStarted?: (requestId: string) => void,
  ): Promise<SentinelWorkspaceDetailWireDto> {
    return this.submitHitlDecision(ticketId, { approvalId, decision: 'reject', notes }, onStarted);
  },

  async submitHitlDecision(
    ticketId: number,
    decision: { approvalId: string; decision: 'approve' | 'reject'; notes?: string },
    onStarted?: (requestId: string) => void,
  ): Promise<SentinelWorkspaceDetailWireDto> {
    const before = await this.getConversation(ticketId);
    const accepted = await api.post<ApiEnvelope<{ requestId: string }>>(
      API_ENDPOINTS.SENTINEL.HITL_DECISION(ticketId),
      decision,
      actionHeaders(),
    );
    onStarted?.(accepted.data.data.requestId);
    return this.waitForArtifact(ticketId, before.ticket.current_ai_event_log_id, {
      requestId: accepted.data.data.requestId,
    });
  },

  /** Run the post-processing actions parked on one turn; the caller refreshes the ticket to show the results. */
  async runPostProcessing(ticketId: number, aiEventLogId: number): Promise<void> {
    await api.post<ApiEnvelope<PendingActionsDto>>(
      API_ENDPOINTS.SENTINEL.POST_PROCESSING_EXECUTE(ticketId),
      { aiEventLogId },
      actionHeaders(),
    );
  },

  async sendResponse(
    ticketId: number,
    input: {
      content: string;
      close: boolean;
      attachments?: SentinelOutboundAttachment[];
      /** Gorgias only: written to the ticket right before the close. */
      ticketFields?: TicketFieldValue[];
      /** The check this content passed, and the reason when a Sentinel overrides a paused one. */
      gate?: { checkId: string; overrideReason?: string };
    },
    idempotencyKey?: string,
  ): Promise<SendResponseOutcome> {
    const payload = {
      ...input,
      ...(input.attachments
        ? { attachments: input.attachments.map(SentinelWorkspaceAdapter.outboundAttachmentToApi) }
        : {}),
    };
    const response = await api.post<ApiEnvelope<SentinelDispatchResult>>(
      API_ENDPOINTS.SENTINEL.SEND_RESPONSE(ticketId),
      payload,
      actionHeaders(idempotencyKey),
    );
    const result = response.data.data;
    if (result.status === 'gate_blocked') {
      return {
        status: 'gate_blocked',
        errorCode: result.errorCode === 'GATE_STALE' ? 'GATE_STALE' : 'GATE_BLOCKED',
        check: result.check ?? null,
      };
    }
    if (result.status !== 'completed') {
      throw new SentinelWorkspaceError(
        sentinelErrorMessage(result.errorCode ?? null, 'dispatch'),
        result.errorCode ?? null,
      );
    }
    return { status: 'completed' };
  },

  /** The send gate's check of a reply before it is sent. */
  async checkResponse(ticketId: number, content: string): Promise<GateCheckDto> {
    const response = await api.post<ApiEnvelope<GateCheckDto>>(API_ENDPOINTS.SENTINEL.CHECK_RESPONSE(ticketId), {
      content,
    });
    return response.data.data;
  },

  async getSendGate(scope?: { bu_id?: string }): Promise<SendGateConfigDto> {
    const response = await api.get<ApiEnvelope<SendGateConfigDto>>(
      withBuScope(API_ENDPOINTS.SENTINEL.SEND_GATE_SETTINGS, scope ?? {}),
    );
    return response.data.data;
  },

  /** Administrators only. Replaces the brand's switch and threshold. */
  async updateSendGate(config: SendGateConfigDto, scope?: { bu_id?: string }): Promise<SendGateConfigDto> {
    const response = await api.put<ApiEnvelope<SendGateConfigDto>>(
      withBuScope(API_ENDPOINTS.SENTINEL.SEND_GATE_SETTINGS, scope ?? {}),
      config,
    );
    return response.data.data;
  },

  async reject(ticketId: number, input: RejectInput, idempotencyKey?: string): Promise<RejectResult> {
    const response = await api.post<ApiEnvelope<RejectResult>>(
      API_ENDPOINTS.SENTINEL.REJECT(ticketId),
      {
        stage: input.stage,
        reason: input.reason === 'others' ? input.otherText || input.reason : input.reason,
        detail: input.otherText,
        providerAgentId: input.providerAgentId,
        tags: input.tags ?? [],
        note: input.note ?? null,
      },
      actionHeaders(idempotencyKey),
    );
    return response.data.data;
  },

  async getTicketTags(ticketId: number): Promise<TicketTagsDto> {
    const response = await api.get<ApiEnvelope<TicketTagsDto>>(API_ENDPOINTS.SENTINEL.TICKET_TAGS(ticketId));
    return response.data.data;
  },

  /** GET /sentinel/tickets/:id/helpdesk-url — null when the helpdesk link can't be built. */
  async getHelpdeskUrl(ticketId: number): Promise<{ ticketUrl: string | null }> {
    const response = await api.get<ApiEnvelope<{ ticketUrl: string | null }>>(API_ENDPOINTS.SENTINEL.HELPDESK_URL(ticketId));
    return response.data.data;
  },

  async getCustomerTickets(ticketId: number): Promise<CustomerTicketPageDto> {
    const response = await api.get<ApiEnvelope<CustomerTicketPageDto>>(API_ENDPOINTS.SENTINEL.CUSTOMER_TICKETS(ticketId));
    return response.data.data;
  },

  async recordResponseFeedback(
    ticketId: number,
    responseId: string,
    input: { verdict: FeedbackVerdict; reasons: string[]; otherText: string | null; rememberScope: string | null },
  ): Promise<void> {
    await api.post(API_ENDPOINTS.SENTINEL.BLUEPRINT_FEEDBACK(ticketId), {
      aiEventLogId: Number(responseId),
      verdict: input.verdict,
      revisionTarget: 'response',
      reasons: input.reasons,
      ...(input.otherText ? { otherText: input.otherText } : {}),
      ...(input.rememberScope ? { rememberScope: input.rememberScope } : {}),
    });
  },

  async addNote(ticketId: number, input: { content: string }): Promise<void> {
    await api.post(API_ENDPOINTS.SENTINEL.ADD_NOTE(ticketId), input, actionHeaders());
  },

  /** A Sentinel takes an unassigned ticket. Rejects with a 409 when someone else got it first. */
  async assignToMe(ticketId: number): Promise<void> {
    await api.post(API_ENDPOINTS.SENTINEL.ASSIGN_TO_ME(ticketId));
  },

  /**
   * Drafts for the customer message a reopened `closed_in_helpdesk` ticket holds. Answers at once;
   * the draft arrives like any other. Rejects with a 409 when Generate no longer applies.
   */
  async generateAgain(ticketId: number): Promise<void> {
    await api.post(API_ENDPOINTS.SENTINEL.GENERATE(ticketId));
  },

  /**
   * Closes the ticket in the helpdesk and in Kim, without sending anything. Rejects with a 409 in
   * review mode or while a reply is being sent, a 502 when the helpdesk refuses the close.
   */
  async closeTicket(ticketId: number): Promise<void> {
    await api.post(API_ENDPOINTS.SENTINEL.CLOSE(ticketId), {}, actionHeaders());
  },

  async assign(_ticketId: number, _input: { reason: RejectInput['reason']; providerAgentId: string }): Promise<void> {
    void _ticketId;
    void _input;
    throw new Error('Sentinel reassignment is not supported; reject and unassign instead');
  },

  async getProviderAgents(ticketId: number): Promise<ProviderAgentPageDto> {
    const response = await api.get<ApiEnvelope<ProviderAgentPageDto>>(API_ENDPOINTS.SENTINEL.PROVIDER_AGENTS(ticketId));
    return response.data.data;
  },

  async getSimilarTicketThread(ticketId: number, conversationId: string): Promise<SimilarTicketThreadDto> {
    const response = await api.get<ApiEnvelope<SimilarTicketThreadDto>>(
      API_ENDPOINTS.SENTINEL.SIMILAR_TICKET_THREAD(ticketId, conversationId),
    );
    return response.data.data;
  },
};
